import React from 'react';

export const LINE_HEIGHT_PX = 27;
export const KV_HIGHLIGHT_NAME = 'kv-search-highlight';

// Inject the ::highlight() CSS rule once into <head> so it is unscoped and
// shared across all component instances. Emotion scopes rules to a generated
// class when using `sx`, which breaks multi-instance highlighting.
const injectHighlightStyle = (): void => {
  const styleId = 'kv-highlight-style';
  if (document.getElementById(styleId)) return;
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `::highlight(${KV_HIGHLIGHT_NAME}) { background-color: rgba(237, 207, 78, 0.7); }`;
  document.head.appendChild(style);
};

// Module-level shared Highlight that all instances contribute ranges to.
// Each instance tracks its own ranges by identity so it can remove them on
// cleanup without affecting other instances.
// Lazily initialized on first registration to avoid module-level `new Highlight()`
// which triggers compat/compat lint errors for environments that don't support it.
const highlightRef = { current: null as Highlight | null };

const getOrCreateSharedHighlight = (): Highlight | null => {
  if (highlightRef.current) return highlightRef.current;
  if (typeof CSS === 'undefined' || !CSS.highlights) return null;
  // eslint-disable-next-line compat/compat
  highlightRef.current = new Highlight();
  CSS.highlights.set(KV_HIGHLIGHT_NAME, highlightRef.current);
  return highlightRef.current;
};

const instanceRanges = new Map<object, Range[]>();

export const registerHighlightRanges = (key: object, ranges: Range[]): void => {
  const highlight = getOrCreateSharedHighlight();
  if (!highlight) return;
  injectHighlightStyle();
  // Remove this instance's previous ranges from the shared Highlight
  const prev = instanceRanges.get(key) ?? [];
  prev.forEach(r => highlight.delete(r));
  // Add new ranges
  ranges.forEach(r => highlight.add(r));
  instanceRanges.set(key, ranges);
};

export const unregisterHighlightRanges = (key: object): void => {
  const highlight = highlightRef.current;
  if (!highlight) return;
  const prev = instanceRanges.get(key) ?? [];
  prev.forEach(r => highlight.delete(r));
  instanceRanges.delete(key);
};

export const tooltipPopperProps = { sx: { '& .MuiTooltip-tooltip': { maxWidth: 800 } } };

const ellipsisStyle = { lineHeight: `${LINE_HEIGHT_PX}px`, fontSize: '13px', cursor: 'default' };

export const EllipsisIndicator: React.FC<{ highlighted: boolean }> = ({ highlighted, ...props }) => (
  <span style={ellipsisStyle} {...props}>
    {highlighted ? <mark>...</mark> : '...'}
  </span>
);

export const hasTermMatch = (entries: [string, unknown][], terms: string[]): boolean => {
  if (!terms.length) return false;
  try {
    const escaped = terms.map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const regex = new RegExp(escaped.join('|'), 'i');
    return entries.some(([, v]) => regex.test(String(v)));
  } catch {
    return false;
  }
};

export const countVisibleKvSpans = (container: HTMLElement, maxHeightPx: number): number => {
  const dl = container.querySelector('dl');
  if (!dl) return 0;

  const subpixelTolerance = 1;
  // [data-kv-span] excludes appended children (e.g. EllipsisIndicator) which have no such attribute
  const kvSpans = dl.querySelectorAll<HTMLElement>(':scope > span[data-kv-span]');
  const dlTop = dl.getBoundingClientRect().top;
  const clampBottom = dlTop + maxHeightPx;

  return Array.from(kvSpans).reduce((acc, span) => {
    const fitsWithinClamp = span.getBoundingClientRect().bottom <= clampBottom + subpixelTolerance;
    return fitsWithinClamp ? acc + 1 : acc;
  }, 0);
};

export const scheduleAfterLayoutSettles = (callback: () => void): (() => void) => {
  // Double rAF ensures layout is fully settled (including table column widths) before measuring.
  const frameRef = { current: 0 };
  frameRef.current = requestAnimationFrame(() => {
    frameRef.current = requestAnimationFrame(callback);
  });
  return () => cancelAnimationFrame(frameRef.current);
};
